import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Columns3, Download, Search } from 'lucide-react'
import { normalizeAlias } from '@/lib/nameNormalization'
import { Button } from '@/components/ui/Button'
import {
  isStickyId,
  stickyCellClass,
  stickyOffsets,
  stickyRightOffsets,
} from '@/components/table/stickyColumns'
import { useDragScroll } from '@/hooks/useDragScroll'
import { TopScrollSync } from './TopScrollSync'

export type ProtoColumn<T> = {
  id: string
  header: string
  width?: number
  sortable?: boolean
  hideable?: boolean
  align?: 'left' | 'right' | 'center'
  cell: (row: T, index: number) => ReactNode
  sortValue?: (row: T) => string | number
}

type ProtoExcelTableProps<T> = {
  columns: ProtoColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  searchPlaceholder?: string
  onRowClick?: (row: T) => void
  onRowContextMenu?: (row: T, e: React.MouseEvent) => void
  toolbarExtra?: ReactNode
  exportName?: string
  /** Client-side page size. Omit or 0 = no pagination. */
  pageSize?: number
  loading?: boolean
  error?: string | null
  emptyMessage?: string
  /** Opt-in sticky identity columns (left). */
  stickyLeftColumnIds?: string[]
  /** Opt-in sticky action columns (right). */
  stickyRightColumnIds?: string[]
  /** Show a display-order number before the selection checkbox. */
  showRowNumbers?: boolean
}

export function normalizeTableSearchText(value: string): string {
  return normalizeAlias(value)
}

const CHECKBOX_W = 32
const ROW_NUMBER_W = 48

export function ProtoExcelTable<T>({
  columns,
  rows,
  rowKey,
  searchPlaceholder = 'Tìm kiếm...',
  onRowClick,
  onRowContextMenu,
  toolbarExtra,
  exportName = 'export',
  pageSize = 0,
  loading = false,
  error = null,
  emptyMessage = 'Không có dữ liệu',
  stickyLeftColumnIds = [],
  stickyRightColumnIds = [],
  showRowNumbers = false,
}: ProtoExcelTableProps<T>) {
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [showCols, setShowCols] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const tableScrollRef = useDragScroll<HTMLDivElement>()

  const visibleCols = columns.filter((c) => !hidden.has(c.id))

  const widthMap = useMemo(() => {
    const m: Record<string, number | undefined> = {}
    for (const c of columns) m[c.id] = c.width
    return m
  }, [columns])

  const leftStickyVisible = stickyLeftColumnIds.filter((id) =>
    visibleCols.some((c) => c.id === id),
  )
  const rightStickyVisible = stickyRightColumnIds.filter((id) =>
    visibleCols.some((c) => c.id === id),
  )
  const anyStickyLeft = leftStickyVisible.length > 0
  const leftOff = stickyOffsets(leftStickyVisible, widthMap)
  const rightOff = stickyRightOffsets(rightStickyVisible, widthMap)

  const filtered = useMemo(() => {
    const q = normalizeTableSearchText(search)
    let list = rows
    if (q) {
      list = rows.filter((row) =>
        visibleCols.some((col) => {
          const raw = col.sortValue?.(row)
          if (raw != null) return normalizeTableSearchText(String(raw)).includes(q)
          return false
        }),
      )
      // fallback: stringify all sort values from all columns
      // fallback: stringify all sort values from all columns
      if (!list.length) {
        list = rows.filter((row) =>
          columns.some((col) =>
            normalizeTableSearchText(String(col.sortValue?.(row) ?? '')).includes(q),
          ),
        )
      }
    }
    if (sortBy) {
      const col = columns.find((c) => c.id === sortBy)
      if (col?.sortValue) {
        list = [...list].sort((a, b) => {
          const va = col.sortValue!(a)
          const vb = col.sortValue!(b)
          const cmp =
            typeof va === 'number' && typeof vb === 'number'
              ? va - vb
              : String(va).localeCompare(String(vb), 'vi')
          return sortDir === 'asc' ? cmp : -cmp
        })
      }
    }
    return list
  }, [rows, search, sortBy, sortDir, columns, visibleCols])

  const pageCount =
    pageSize > 0 ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1
  const safePage = Math.min(page, pageCount)
  const paged =
    pageSize > 0
      ? filtered.slice((safePage - 1) * pageSize, safePage * pageSize)
      : filtered

  // Reset page when filter/search changes
  useEffect(() => {
    setPage(1)
  }, [search, rows.length])

  function toggleSort(id: string) {
    if (sortBy === id) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortBy(id)
      setSortDir('asc')
    }
  }

  function exportCsv() {
    const headers = [...(showRowNumbers ? ['STT'] : []), ...visibleCols.map((c) => c.header)]
    const lines = filtered.map((row, index) =>
      [...(showRowNumbers ? [String(index + 1)] : []), ...visibleCols
        .map((c) => {
          const v = c.sortValue?.(row) ?? ''
          return `"${String(v).replace(/"/g, '""')}"`
        })]
        .join(','),
    )
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${exportName}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function toggleSelect(key: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function colStyle(col: ProtoColumn<T>): CSSProperties {
    const side = isStickyId(col.id, leftStickyVisible, rightStickyVisible)
    const style: CSSProperties = {}
    if (col.width) {
      style.width = col.width
      style.minWidth = col.width
    }
    if (side === 'left') {
      style.left = (anyStickyLeft ? CHECKBOX_W : 0) + (showRowNumbers ? ROW_NUMBER_W : 0) + (leftOff[col.id] ?? 0)
    }
    if (side === 'right') {
      style.right = rightOff[col.id] ?? 0
    }
    return style
  }

  function colClass(col: ProtoColumn<T>, isHeader: boolean): string {
    const side = isStickyId(col.id, leftStickyVisible, rightStickyVisible)
    const align =
      col.align === 'right'
        ? 'text-right'
        : col.align === 'center'
          ? 'text-center'
          : 'text-left'
    const wrap = isHeader ? 'break-words leading-tight align-top' : 'whitespace-nowrap'
    return `px-2 py-2 ${wrap} ${align} ${stickyCellClass({ side, isHeader })}`
  }

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-border)] bg-slate-50 px-3 py-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white pr-2 pl-8 text-sm outline-none focus:border-[var(--color-accent)]"
          />
        </div>
        <div className="relative">
          <Button size="sm" variant="secondary" onClick={() => setShowCols((v) => !v)}>
            <Columns3 className="h-3.5 w-3.5" />
            Cột
          </Button>
          {showCols ? (
            <div className="absolute right-0 z-40 mt-1 w-48 rounded border border-slate-200 bg-white p-2 shadow-lg">
              {columns.map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-1 py-1 text-xs">
                  <input
                    type="checkbox"
                    checked={!hidden.has(c.id)}
                    disabled={c.hideable === false}
                    onChange={() => {
                      setHidden((prev) => {
                        const next = new Set(prev)
                        if (next.has(c.id)) next.delete(c.id)
                        else next.add(c.id)
                        return next
                      })
                    }}
                  />
                  {c.header}
                </label>
              ))}
            </div>
          ) : null}
        </div>
        <Button size="sm" variant="secondary" onClick={exportCsv}>
          <Download className="h-3.5 w-3.5" />
          Excel
        </Button>
        {toolbarExtra}
        <span className="ml-auto text-xs text-slate-500">
          {selected.size ? `${selected.size} chọn · ` : ''}
          {filtered.length}/{rows.length}
        </span>
      </div>

      {error ? (
        <div className="border-b border-red-100 bg-red-50 px-3 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {/* Horizontal scrollbar synced on top */}
      <TopScrollSync targetRef={tableScrollRef} />

      {/* Horizontal scroll only inside table region — supports mouse drag scroll */}
      <div ref={tableScrollRef} className="max-h-[calc(100vh-280px)] overflow-auto">
        <table className="w-full min-w-[960px] border-collapse text-left text-sm">
          <thead className="sticky top-0 z-10 bg-slate-100">
            <tr className="border-b border-slate-300 text-xs font-semibold tracking-wide text-slate-600 uppercase">
              {showRowNumbers ? (
                <th
                  className={`w-12 min-w-12 px-2 py-2 text-center ${stickyCellClass({ side: 'left', isHeader: true })}`}
                  style={{ left: 0, width: ROW_NUMBER_W, minWidth: ROW_NUMBER_W }}
                >
                  STT
                </th>
              ) : null}
              <th
                className={`w-8 px-2 py-2 ${
                  anyStickyLeft || showRowNumbers
                    ? stickyCellClass({ side: 'left', isHeader: true })
                    : ''
                }`}
                style={anyStickyLeft || showRowNumbers
                  ? { left: showRowNumbers ? ROW_NUMBER_W : 0, minWidth: CHECKBOX_W }
                  : undefined}
              >
                <input
                  type="checkbox"
                  checked={
                    paged.length > 0 && paged.every((r) => selected.has(rowKey(r)))
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelected(new Set(paged.map(rowKey)))
                    } else setSelected(new Set())
                  }}
                />
              </th>
              {visibleCols.map((col) => (
                <th
                  key={col.id}
                  className={colClass(col, true)}
                  style={colStyle(col)}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 uppercase hover:text-slate-900"
                      onClick={() => toggleSort(col.id)}
                    >
                      {col.header}
                      {sortBy === col.id ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={visibleCols.length + 1 + (showRowNumbers ? 1 : 0)}
                  className="px-3 py-10 text-center text-slate-500"
                >
                  Đang tải...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleCols.length + 1 + (showRowNumbers ? 1 : 0)}
                  className="px-3 py-10 text-left text-slate-500"
                >
                  <div className="sticky left-0 w-fit max-w-[min(100%,48rem)] pl-2">{emptyMessage}</div>
                </td>
              </tr>
            ) : (
              paged.map((row, rowIdx) => {
                const key = rowKey(row)
                return (
                  <tr
                    key={key}
                    className={`group border-b border-slate-100 hover:bg-[var(--color-bg)] ${
                      onRowClick ? 'cursor-pointer' : ''
                    } ${selected.has(key) ? 'row-selected bg-[var(--color-accent-soft)]' : ''}`}
                    onClick={() => onRowClick?.(row)}
                    onContextMenu={(e) => {
                      if (onRowContextMenu) {
                        e.preventDefault()
                        onRowContextMenu(row, e)
                      }
                    }}
                    onKeyDown={(e) => {
                      if (onRowContextMenu && (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10'))) {
                        e.preventDefault()
                        const rect = e.currentTarget.getBoundingClientRect()
                        onRowContextMenu(row, { clientX: rect.left + 60, clientY: rect.top + 20 } as any)
                      }
                    }}
                  >
                    {showRowNumbers ? (
                      <td
                        className={`w-12 min-w-12 px-2 py-1.5 text-center tabular-nums ${stickyCellClass({ side: 'left', isHeader: false })}`}
                        style={{ left: 0, width: ROW_NUMBER_W, minWidth: ROW_NUMBER_W }}
                      >
                        {(pageSize > 0 ? (safePage - 1) * pageSize : 0) + rowIdx + 1}
                      </td>
                    ) : null}
                    <td
                      className={`px-2 py-1.5 ${
                        anyStickyLeft || showRowNumbers
                          ? stickyCellClass({ side: 'left', isHeader: false })
                          : ''
                      }`}
                      style={anyStickyLeft || showRowNumbers
                        ? { left: showRowNumbers ? ROW_NUMBER_W : 0, minWidth: CHECKBOX_W }
                        : undefined}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggleSelect(key)}
                      />
                    </td>
                    {visibleCols.map((col) => (
                      <td
                        key={col.id}
                        className={`px-2 py-1.5 ${
                          col.align === 'right'
                            ? 'text-right tabular-nums'
                            : col.align === 'center'
                              ? 'text-center'
                              : ''
                        } ${stickyCellClass({
                          side: isStickyId(
                            col.id,
                            leftStickyVisible,
                            rightStickyVisible,
                          ),
                          isHeader: false,
                        })}`}
                        style={colStyle(col)}
                      >
                        {col.cell(row, (pageSize > 0 ? (safePage - 1) * pageSize : 0) + rowIdx)}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {pageSize > 0 && !loading && filtered.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--color-border)] bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <span>
            Trang {safePage}/{pageCount} · {pageSize} dòng/trang
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="secondary"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Trước
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={safePage >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            >
              Sau
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
